"use client";

import React, { useCallback, useEffect, useState } from "react";
import { Copy, Link2, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/components/ui/toast";

interface CompanySupportLinkCardProps {
  companyId: string;
  companyName?: string | null;
}

type LoadState =
  | { phase: "loading" }
  | { phase: "ready"; exists: boolean; url: string | null }
  | { phase: "error" };

interface SupportLinkResponse {
  exists?: boolean;
  url?: string | null;
}

/**
 * Company detail "Support Link" card. Shows the per-company anonymous
 * ticket-submission URL (fetched + decrypted server-side on demand), with copy
 * and (re)generate actions. The link lets a client's staff file support tickets
 * with WTC without a Hub login — share it freely with the company.
 */
export function CompanySupportLinkCard({ companyId, companyName }: CompanySupportLinkCardProps) {
  const { showToast } = useToast();
  const [state, setState] = useState<LoadState>({ phase: "loading" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setState({ phase: "loading" });
    try {
      const res = await fetch(`/api/companies/${encodeURIComponent(companyId)}/support-link`, {
        cache: "no-store",
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      const json = (await res.json()) as SupportLinkResponse;
      setState({ phase: "ready", exists: Boolean(json.exists), url: json.url ?? null });
    } catch {
      setState({ phase: "error" });
    }
  }, [companyId]);

  useEffect(() => {
    void load();
  }, [load]);

  const mint = useCallback(
    async (action: "generate" | "regenerate") => {
      setBusy(true);
      try {
        const res = await fetch(`/api/companies/${encodeURIComponent(companyId)}/support-link`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action }),
        });
        if (!res.ok) throw new Error(`status ${res.status}`);
        const json = (await res.json()) as SupportLinkResponse;
        setState({ phase: "ready", exists: Boolean(json.exists), url: json.url ?? null });
        showToast({
          type: "success",
          title: action === "regenerate" ? "Support link regenerated" : "Support link created",
          description: "Share it with the client so their team can file tickets without a login.",
        });
      } catch {
        showToast({
          type: "error",
          title: "Couldn't update the support link",
          description: "Please try again in a moment.",
        });
      } finally {
        setBusy(false);
      }
    },
    [companyId, showToast],
  );

  const copy = useCallback(async () => {
    if (state.phase !== "ready" || !state.url) return;
    try {
      await navigator.clipboard.writeText(state.url);
      showToast({ type: "success", title: "Copied", description: "Support link copied to clipboard." });
    } catch {
      showToast({ type: "error", title: "Couldn't copy", description: "Copy the link manually." });
    }
  }, [state, showToast]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Link2 className="w-5 h-5 text-gray-600 dark:text-gray-400 shrink-0" />
        <h3 className="text-base md:text-lg font-semibold text-card-foreground">Support Link</h3>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <p className="text-sm text-muted-foreground">
            A private, no-login web page where{" "}
            <span className="font-medium text-card-foreground">{companyName || "this company"}</span>
            's team can file support tickets with West Tennessee Consulting. Tickets come in as a
            new contact (name + email) on this company. Share this link with the client to use on a
            go-forward basis.
          </p>

          {state.phase === "loading" && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading…
            </div>
          )}

          {state.phase === "error" && (
            <div className="space-y-3">
              <p className="text-sm text-destructive">We couldn't load the support link.</p>
              <Button type="button" variant="outline" size="sm" onClick={() => void load()}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry
              </Button>
            </div>
          )}

          {state.phase === "ready" && state.exists && state.url && (
            <div className="space-y-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <input
                  readOnly
                  value={state.url}
                  onFocus={(e) => e.currentTarget.select()}
                  className="w-full rounded-md border border-input bg-muted/40 px-3 py-2 text-sm font-mono text-card-foreground"
                />
                <Button type="button" variant="outline" size="sm" onClick={() => void copy()} className="shrink-0">
                  <Copy className="w-4 h-4 mr-2" />
                  Copy
                </Button>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => void mint("regenerate")}
                disabled={busy}
                className="text-muted-foreground"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Regenerate (revokes the old link)
              </Button>
            </div>
          )}

          {state.phase === "ready" && !state.exists && (
            <Button type="button" onClick={() => void mint("generate")} disabled={busy}>
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
              Generate support link
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
